import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Drawer, Button, Typography, Input } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { getCountries } from 'shared/config/store/reducers/countriesSlice';
import PhoneInput, { PhoneInputState } from 'shared/ui/PhoneInput/PhoneInput';
import {
  useRequestPhoneChangeMutation,
  useResendPhoneRequestMutation,
  useConfirmPhoneChangeMutation,
} from '../api';
import CodeInput, { CodeInputRef } from 'shared/ui/CodeInput/CodeInput';
import { useGetCountriesQuery } from 'entities/countries/api';
import styles from './ChangePhoneDrawer.module.scss';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { CheckOutlined } from '@ant-design/icons';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
};

const ChangePhoneDrawer = ({ open, onClose }: Props) => {
  const { formatMessage: t } = useIntl();
  const { showError } = useMessage();

  const user = useSelector(getUserMeta);
  const reduxCountries = useSelector(getCountries);

  const { data: countriesResponse } = useGetCountriesQuery();
  const apiCountries = countriesResponse?.response ?? [];

  const countries = reduxCountries.length === 0 ? apiCountries : reduxCountries;

  const phoneCountry = useMemo(() => {
    return countries.find((country) => country.id === user.phoneCountry)?.phoneCode;
  }, [countries, user.phoneCountry]);

  const [step, setStep] = useState(1);
  const [newPhone, setNewPhone] = useState<PhoneInputState>({
    countryCode: undefined,
    phoneNumber: '',
    haveErrors: true,
  });

  const [requestPhoneChange] = useRequestPhoneChangeMutation();
  const [resendPhoneRequest] = useResendPhoneRequestMutation();
  const [confirmPhoneChange] = useConfirmPhoneChangeMutation();

  const [requestId, setRequestId] = useState<number | null>(null);
  const codeInputRef = useRef<CodeInputRef>(null);

  const handlePhoneChange = useCallback((state: PhoneInputState) => {
    setNewPhone(state);
  }, []);

  const goNext = useCallback(() => setStep((prev) => Math.min(prev + 1, 4)), []);


  const resetAndClose = () => {
    onClose();
    setStep(1);
  };

  const handlePhoneSubmit = useCallback(async () => {
    if (newPhone.countryCode === undefined) {
      showError(t({ id: 'phoneChange.errors.unknown' }));
      return;
    }

    try {
      const { response } = await requestPhoneChange({
        phone: newPhone.phoneNumber,
        phoneCountry: newPhone.countryCode,
      }).unwrap();

      if (response?.id) {
        setRequestId(response.id);
        goNext();
      } else {
        showError(t({ id: 'phoneChange.errors.unknown' }));
      }
    } catch (err: any) {
      console.error('Failed to request phone change:', err);

      const errors = err?.data?.errors;
      if (errors?.length) {
        switch (errors[0].id) {
          case 'sms.blocked':
            showError(t({ id: 'phoneChange.errors.smsBlocked' }));
            break;
          case 'error.phone.length':
            showError(t({ id: 'phoneChange.errors.phoneLength' }));
            break;
          case 'schemas.exists':
            showError(t({ id: 'phoneChange.errors.phoneExists' }));
            break;
          case 'error.limit.change.phone':
            showError(t({ id: 'phoneChange.errors.changeLimitExceeded' }));
            break;
          default:
            showError(t({ id: 'phoneChange.errors.unknown' }));
        }
      } else {
        showError(t({ id: 'phoneChange.errors.network' }));
      }
    }
  }, [newPhone, requestPhoneChange, t, goNext, showError]);

  const handleCodeConfirm = useCallback(
    async (code: string) => {
      if (!requestId) return;

      try {
        await confirmPhoneChange({
          requestId,
          code,
        }).unwrap();

        codeInputRef.current?.success();
        goNext();
      } catch (error) {
        console.error('Invalid code:', error);
        codeInputRef.current?.error(t({ id: 'changePhone.drawer.invalidCode' }));
      }
    },
    [requestId, confirmPhoneChange, goNext, t]
  );

  const handleCodeResend = useCallback(() => {
    if (!requestId) return;
    resendPhoneRequest({ requestId });
  }, [requestId, resendPhoneRequest]);


  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className={styles.centered}>
            <div className={styles.iconWrapper}>
              <PhoneOutlined style={{ fontSize: 40, marginBottom: 8 }} />
            </div>
            <Text className={styles.oldPhoneText}>
                {t({ id: 'changePhone.drawer.yourPhone' })}
            </Text>
            <div className={styles.oldPhoneBlock}>
              <Input
                value={`+${phoneCountry} ${user.phone}`}
                disabled
                bordered={false}
                className={styles.oldPhoneInput}
              />
            </div>

            <Button 
              id="btn__account-name-submit"
              variant="solid"
              color='default'
              onClick={goNext}>
              {t({ id: 'changePhone.drawer.changePhone' })}
            </Button>
          </div>
        );

      case 2:
        return (
          <div className={styles.centered}>
            <Typography.Title level={5} className={styles.mb12}>
              {t({ id: 'changePhone.drawer.enterNewPhone' })}
            </Typography.Title>
            <div className={styles.mb24}>
              <PhoneInput countries={countries} onChange={handlePhoneChange} />
            </div>
            <Button
              id="btn__account-name-submit"
              variant="solid"
              color='default'
              disabled={newPhone.haveErrors}
              onClick={handlePhoneSubmit}
            >
              {t({ id: 'changePhone.drawer.continue' })}
            </Button>
          </div>
        );

      case 3:
        return (
          <div className={styles.centered}>
            <Typography.Title level={5} className={styles.mb12}>
              {t({ id: 'changePhone.drawer.enterCodeSent' })}
            </Typography.Title>
            <Typography.Text type="secondary" className={styles.mb24}>
              {t({ id: 'changePhone.drawer.codeSentMessage' })}
            </Typography.Text>
            <CodeInput
              ref={codeInputRef}
              isPhone
              onEnter={handleCodeConfirm}
              onResend={handleCodeResend}
            />
          </div>
        );

      case 4:
        return (
          <div className={`${styles.centered} ${styles.pt40}`}>
            <div className={styles.successIcon}>
              <CheckOutlined className={styles.successIconCheck} />
            </div>
            <Typography.Title level={4} className={styles.mb24}>
              {t({ id: 'changePhone.drawer.phoneChanged' })}
            </Typography.Title>
            <Button 
              id="btn__account-name-submit"
              variant="solid"
              color='default'
              onClick={resetAndClose}>
              {t({ id: 'changePhone.drawer.done' })}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Drawer
      title={t({ id: 'changePhone.drawer.changePhone' })}
      placement="right"
      onClose={resetAndClose}
      open={open}
      width={500}
      closeIcon={true}
      bodyStyle={{ padding: 24 }}
    >
      {renderStepContent()}
    </Drawer>
  );
};

export default ChangePhoneDrawer;
